package features.profile.ui

import kotlinx.html.*
import kotlinx.html.dom.append
import kotlinx.html.js.onClickFunction
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLInputElement
import AppState
import Router
import features.auth.AuthFeature
import shared.ui.ModalManager

object ProfilePage {
    fun render(root: HTMLElement) {
        val user = AppState.currentUser
        if (user == null) {
            Router.navigateTo("login")
            return
        }
        
        root.innerHTML = ""
        root.append {
            div {
                // Navbar
                shared.ui.components.Navbar.render(this, "Profile", showBackButton = false)
                
                // Main Content
                div("max-w-3xl mx-auto px-4 py-8 pb-32") {
                    // Profile Header
                    div("bg-dark-800 rounded-2xl border border-dark-700 p-8 mb-6") {
                        div("flex flex-col items-center text-center") {
                            // Avatar
                            div("relative mb-4") {
                                img(src = user.avatar ?: "https://ui-avatars.com/api/?name=${user.name}&background=06b6d4&color=fff") {
                                    classes = setOf("w-32", "h-32", "rounded-full", "border-4", "border-brand-cyan")
                                }
                                button(classes = "absolute bottom-0 right-0 bg-brand-cyan hover:bg-brand-green text-white w-10 h-10 rounded-full flex items-center justify-center transition shadow-lg") {
                                    id = "change-avatar-btn"
                                    onClickFunction = {
                                        ModalManager.show("edit-avatar-modal")
                                    }
                                    +"📷"
                                }
                            }
                            
                            h2("text-3xl font-bold mb-1") { +user.name }
                            p("text-slate-400") { +user.email }
                        }
                    }
                    
                    // Profile Info
                    div("bg-dark-800 rounded-2xl border border-dark-700 p-6 mb-6") {
                        h3("text-xl font-bold mb-4") { +"Account Information" }
                        
                        div("space-y-4") {
                            // Name
                            div {
                                label("block text-sm font-medium text-slate-400 mb-2") {
                                    htmlFor = "profile-name"
                                    +"Full Name"
                                }
                                div("flex gap-2") {
                                    input(type = InputType.text, classes = "flex-1 bg-dark-700 border border-dark-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-brand-cyan") {
                                        id = "profile-name"
                                        value = user.name
                                        disabled = true
                                    }
                                    button(classes = "bg-brand-cyan hover:bg-brand-green text-white px-4 py-2 rounded-lg font-medium transition") {
                                        onClickFunction = {
                                            ModalManager.show("edit-name-modal")
                                        }
                                        +"Edit"
                                    }
                                }
                            }
                            
                            // Email
                            div {
                                label("block text-sm font-medium text-slate-400 mb-2") {
                                    htmlFor = "profile-email"
                                    +"Email Address"
                                }
                                div("flex gap-2") {
                                    input(type = InputType.email, classes = "flex-1 bg-dark-700 border border-dark-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-brand-cyan") {
                                        id = "profile-email"
                                        value = user.email
                                        disabled = true
                                    }
                                    button(classes = "bg-dark-700 text-slate-400 px-4 py-2 rounded-lg font-medium cursor-not-allowed") {
                                        disabled = true
                                        +"Locked"
                                    }
                                }
                            }
                        }
                    }
                    
                    // Logout Button
                    div("bg-dark-800 rounded-2xl border border-dark-700 p-6") {
                        button(classes = "w-full bg-red-500/20 hover:bg-red-500/30 text-red-400 px-6 py-3 rounded-lg font-semibold transition border border-red-500/30") {
                            onClickFunction = {
                                val confirmed = kotlinx.browser.window.confirm("Are you sure you want to logout?")
                                if (confirmed) {
                                    AuthFeature.logout()
                                }
                            }
                            +"🚪 Logout"
                        }
                    }
                }
                
                // Bottom Navigation
                shared.ui.components.BottomNav.render(this)
            }
        }
        
        // Setup modals
        setupModals()
    }
    
    private fun setupModals() {
        // Edit Name Modal
        val editNameModal = kotlinx.browser.document.getElementById("edit-name-modal")
        if (editNameModal == null) {
            kotlinx.browser.document.body?.append {
                // Modal Overlay
                div("modal-overlay fixed inset-0 bg-black/50 z-[100] hidden") {
                    id = "edit-name-modal-overlay"
                    onClickFunction = { ModalManager.close() }
                }
                
                // Edit Name Modal Content
                div("modal-content fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-dark-800 border border-dark-700 rounded-2xl p-6 w-full max-w-md z-[101] hidden shadow-2xl scale-95 opacity-0 transition-all duration-300") {
                    id = "edit-name-modal"
                    
                    div("flex justify-between items-center mb-6") {
                        h3("text-xl font-bold") { +"Edit Name" }
                        button(classes = "text-slate-400 hover:text-white") {
                            onClickFunction = { ModalManager.close() }
                            +"✕"
                        }
                    }
                    
                    input(type = InputType.text, classes = "w-full bg-dark-900 border border-dark-700 rounded-xl p-3 text-white focus:outline-none focus:border-brand-cyan mb-6") {
                        id = "edit-name-input"
                        placeholder = "Enter new name"
                        value = AppState.currentUser?.name ?: ""
                    }
                    
                    div("flex justify-end gap-3") {
                        button(classes = "px-4 py-2 rounded-xl text-slate-300 hover:bg-dark-700 transition") {
                            onClickFunction = { ModalManager.close() }
                            +"Cancel"
                        }
                        button(classes = "px-4 py-2 rounded-xl bg-brand-cyan text-white hover:bg-brand-green transition") {
                            onClickFunction = {
                                val input = kotlinx.browser.document.getElementById("edit-name-input") as? HTMLInputElement
                                val newName = input?.value ?: ""
                                if (newName.isNotBlank()) {
                                    features.profile.handlers.ProfileHandlers.updateName(newName)
                                }
                            }
                            +"Save"
                        }
                    }
                }
                
                // Modal Overlay for Avatar
                div("modal-overlay fixed inset-0 bg-black/50 z-[100] hidden") {
                    id = "edit-avatar-modal-overlay"
                    onClickFunction = { ModalManager.close() }
                }
                
                // Edit Avatar Modal Content
                div("modal-content fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-dark-800 border border-dark-700 rounded-2xl p-6 w-full max-w-md z-[101] hidden shadow-2xl scale-95 opacity-0 transition-all duration-300") {
                    id = "edit-avatar-modal"
                    
                    div("flex justify-between items-center mb-6") {
                        h3("text-xl font-bold") { +"Change Avatar" }
                        button(classes = "text-slate-400 hover:text-white") {
                            onClickFunction = { ModalManager.close() }
                            +"✕"
                        }
                    }
                    
                    p("text-slate-400 mb-4 text-sm") {
                        +"Enter image URL or upload a photo"
                    }
                    
                    input(type = InputType.text, classes = "w-full bg-dark-900 border border-dark-700 rounded-xl p-3 text-white focus:outline-none focus:border-brand-cyan mb-6") {
                        id = "edit-avatar-input"
                        placeholder = "https://example.com/avatar.jpg"
                        value = AppState.currentUser?.avatar ?: ""
                    }
                    
                    div("flex justify-end gap-3") {
                        button(classes = "px-4 py-2 rounded-xl text-slate-300 hover:bg-dark-700 transition") {
                            onClickFunction = { ModalManager.close() }
                            +"Cancel"
                        }
                        button(classes = "px-4 py-2 rounded-xl bg-brand-cyan text-white hover:bg-brand-green transition") {
                            onClickFunction = {
                                val input = kotlinx.browser.document.getElementById("edit-avatar-input") as? HTMLInputElement
                                val newAvatar = input?.value ?: ""
                                if (newAvatar.isNotBlank()) {
                                    features.profile.handlers.ProfileHandlers.updateAvatar(newAvatar)
                                }
                            }
                            +"Save"
                        }
                    }
                }
            }
        }
    }
}
